import { useState, useEffect } from 'react';
import { s3PresignedUrlService } from '../services';

/**
 * Custom hook for managing presigned URLs for event media
 * 
 * Automatically handles:
 * - Fetching presigned URLs from backend
 * - Caching URLs to reduce API calls
 * - Detecting S3 URLs that need presigning
 * - Falling back to direct URLs for non-S3 media
 * 
 * @param eventId - Event ID
 * @param mediaType - Type of media ('snapshot', 'video', or 'thumbnail')
 * @param directUrl - Direct URL from event (might be S3 or external)
 * @returns Object with presigned URL, loading state, and error
 */
export function useEventPresignedUrl(
  eventId: number | undefined,
  mediaType: 'snapshot' | 'video' | 'thumbnail',
  directUrl: string | null | undefined
) {
  const requestKey = JSON.stringify([eventId, mediaType, directUrl]);
  const [media, setMedia] = useState({ key: '', url: '', loading: false, error: null as string | null });
  const privateMedia = !!directUrl && isPrivateS3Url(directUrl);

  useEffect(() => {
    if (!directUrl || !eventId) {
      setMedia({ key: requestKey, url: '', loading: false, error: null });
      return;
    }
    if (!privateMedia) {
      setMedia({ key: requestKey, url: directUrl, loading: false, error: null });
      return;
    }
    let active = true;
    setMedia({ key: requestKey, url: '', loading: true, error: null });
    s3PresignedUrlService.getPresignedUrl(eventId, mediaType).then(url => {
      if (active) setMedia({ key: requestKey, url: url || directUrl, loading: false, error: null });
    }).catch(err => {
      if (active) setMedia({ key: requestKey, url: directUrl, loading: false,
        error: err instanceof Error ? err.message : 'Failed to load media' });
    });
    return () => { active = false; };
  }, [eventId, mediaType, directUrl, requestKey, privateMedia]);

  // A parameter change must never render the previous event's signed image
  // while its replacement request is still being started or completed.
  if (media.key !== requestKey) {
    return { presignedUrl: directUrl && eventId && !privateMedia ? directUrl : '',
      isLoading: !!(directUrl && eventId && privateMedia), error: null };
  }
  return { presignedUrl: media.url, isLoading: media.loading, error: media.error };
}

/**
 * Check if URL is a private S3 URL that needs presigning
 * 
 * Returns true for:
 * - https://bucket.s3.region.amazonaws.com/key (no query params)
 * - https://s3.region.amazonaws.com/bucket/key (no query params)
 * 
 * Returns false for:
 * - Already signed URLs (contain X-Amz-Signature or similar)
 * - External URLs (HikVision, EZVIZ, etc.)
 * - Non-S3 URLs
 */
function isPrivateS3Url(url: string): boolean {
  if (!url) return false;

  // Already signed URL (has query parameters with AWS signature)
  if (url.includes('?') && (url.includes('X-Amz-') || url.includes('Signature='))) {
    return false;
  }

  // External cloud URLs (HikVision, EZVIZ, etc.)
  if (url.includes('ezviz') || url.includes('hpc-') || url.includes('hikvision')) {
    return false;
  }

  // Check if it's an S3 URL without query parameters
  const isS3 = url.includes('.s3.') || url.includes('s3.amazonaws.com');
  const hasNoParams = !url.includes('?');

  return isS3 && hasNoParams;
}

/**
 * Preload presigned URLs for multiple events
 * Use this to warm up the cache when loading a page with multiple events
 */
export async function preloadEventUrls(
  eventIds: number[],
  mediaType: 'snapshot' | 'video' | 'thumbnail' = 'snapshot'
): Promise<void> {
  return s3PresignedUrlService.preloadEventUrls(eventIds, mediaType);
}
